import {QueryClient, useQueryClient} from '@tanstack/react-query';
import {useCallback, useMemo} from 'react';
import proposalUpdaters from '@/api/updaters/proposals';
import type {
  CampaignCreatorApiResponse,
  CampaignCreatorRow,
} from '@/views/campaigns/creators-tab/types/creatorTabTypes';

export interface UseShortlistResult {
  count: number;
  isShortlisted: (creatorId: string) => boolean;
  toggle: (creatorId: string) => void;
  addMany: (creatorIds: string[]) => void;
  removeMany: (creatorIds: string[]) => void;
  areAllShortlisted: (creatorIds: string[]) => boolean;
}

function applyOptimisticUpdate(
  queryClient: QueryClient,
  campaignId: string,
  ids: string[],
  value: boolean
) {
  const idSet = new Set(ids);
  queryClient.setQueriesData<{
    pages?: {data: CampaignCreatorApiResponse[]}[];
    pageParams?: unknown[];
  }>({queryKey: ['campaignV2Creators', {campaignId}], exact: false}, (old) => {
    if (!old?.pages) return old;
    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        data: page.data.map((c) =>
          idSet.has(c.id) ? {...c, isShortlisted: value} : c
        ),
      })),
    };
  });
}

function invalidateCreators(
  queryClient: QueryClient,
  campaignId: string | undefined
) {
  queryClient.invalidateQueries({
    queryKey: ['campaignV2Creators', {campaignId}],
  });
  // The campaign record carries server-computed shortlist aggregates
  // (`shortlistedProposalsTotalPrice`) shown in the campaign cost summary,
  // so it must refetch alongside the creator rows.
  queryClient.invalidateQueries({queryKey: ['campaign', campaignId]});
}

/**
 * Hook for reading and mutating the shortlist for a single campaign.
 *
 * Shortlisted state is derived from the `isShortlisted` field on each creator
 * row returned by the API. Mutations call the backend and apply optimistic
 * updates to the cached creator list, then invalidate to refetch.
 *
 * @param campaignId The campaign whose shortlist we're reading/writing.
 * @param creators   The creator rows currently in scope. Determines which IDs
 *   are treated as shortlisted and provides the creatorId → paidGigId map
 *   needed for API calls.
 */
function useShortlistUpdate(
  campaignId: string | undefined,
  creators: CampaignCreatorRow[] = []
): UseShortlistResult {
  const queryClient = useQueryClient();

  const paidGigIdMap = useMemo(() => {
    const map = new Map<string, string>();
    creators.forEach((c) => {
      if (c.paidGigId) map.set(c.id, c.paidGigId);
    });
    return map;
  }, [creators]);

  const toggle = useCallback(
    (creatorId: string) => {
      if (!campaignId) return;
      const paidGigId = paidGigIdMap.get(creatorId);
      if (!paidGigId) return;
      const willBeShortlisted = !creators.find((c) => c.id === creatorId)
        ?.isShortlisted;
      applyOptimisticUpdate(
        queryClient,
        campaignId,
        [creatorId],
        willBeShortlisted
      );
      // Direct call rather than useMutation: addMany/removeMany need to fire
      // N requests in parallel (Promise.all), which useMutation's single-call
      // model doesn't handle cleanly. Using the same raw-call pattern here
      // keeps toggle/addMany/removeMany consistent.
      const call = willBeShortlisted
        ? proposalUpdaters.addToShortlist({paidGigId})
        : proposalUpdaters.removeFromShortlist({paidGigId});
      call.finally(() => invalidateCreators(queryClient, campaignId));
    },
    [campaignId, creators, paidGigIdMap, queryClient]
  );

  const addMany = useCallback(
    (creatorIds: string[]) => {
      if (!campaignId || creatorIds.length === 0) return;
      const pairs = creatorIds.flatMap((id) => {
        const paidGigId = paidGigIdMap.get(id);
        return paidGigId ? [{id, paidGigId}] : [];
      });
      if (pairs.length === 0) return;
      applyOptimisticUpdate(
        queryClient,
        campaignId,
        pairs.map((p) => p.id),
        true
      );
      Promise.all(
        pairs.map((p) =>
          proposalUpdaters.addToShortlist({paidGigId: p.paidGigId})
        )
      ).finally(() => invalidateCreators(queryClient, campaignId));
    },
    [campaignId, paidGigIdMap, queryClient]
  );

  const removeMany = useCallback(
    (creatorIds: string[]) => {
      if (!campaignId || creatorIds.length === 0) return;
      const pairs = creatorIds.flatMap((id) => {
        const paidGigId = paidGigIdMap.get(id);
        return paidGigId ? [{id, paidGigId}] : [];
      });
      if (pairs.length === 0) return;
      applyOptimisticUpdate(
        queryClient,
        campaignId,
        pairs.map((p) => p.id),
        false
      );
      Promise.all(
        pairs.map((p) =>
          proposalUpdaters.removeFromShortlist({paidGigId: p.paidGigId})
        )
      ).finally(() => invalidateCreators(queryClient, campaignId));
    },
    [campaignId, paidGigIdMap, queryClient]
  );

  const isShortlisted = useCallback(
    (creatorId: string) =>
      creators.find((c) => c.id === creatorId)?.isShortlisted ?? false,
    [creators]
  );

  const areAllShortlisted = useCallback(
    (creatorIds: string[]) =>
      creatorIds.length > 0 &&
      creatorIds.every(
        (id) => creators.find((c) => c.id === id)?.isShortlisted ?? false
      ),
    [creators]
  );

  return {
    count: creators.filter((c) => c.isShortlisted).length,
    isShortlisted,
    toggle,
    addMany,
    removeMany,
    areAllShortlisted,
  };
}

export default useShortlistUpdate;
